import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PageHeader } from "@/components/PageHeader";
import { Field } from "./Fleet";
import { Plus, X, Trash2, KeyRound, Pencil } from "lucide-react";
import { ROLE_LABEL } from "@/lib/format";

export default function Users() {
  const { user, company } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader
        tag="Equipo"
        title="Usuarios de mi empresa"
        description={`Administra los usuarios de ${company?.name || "tu empresa"}. Todos los usuarios creados aquí pertenecen al mismo tenant.`}
        action={
          <button onClick={() => setCreating(true)} className="h-touch px-6 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 active:scale-[0.98] flex items-center gap-2" data-testid="new-user-btn">
            <Plus size={18} strokeWidth={2.5} /> Nuevo Usuario
          </button>
        }
      />

      <div className="border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr><Th>Nombre</Th><Th>Correo</Th><Th>Rol</Th><Th>Creado</Th><Th></Th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-gray-200" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 text-sm font-bold">{u.name}{u.id === user.id && <span className="ml-2 text-xs text-blue-600">(tú)</span>}</td>
                <td className="px-4 py-3 text-sm font-mono">{u.email}</td>
                <td className="px-4 py-3 text-xs font-bold uppercase tracking-wider">{ROLE_LABEL[u.role]}</td>
                <td className="px-4 py-3 text-xs font-mono text-gray-500">{(u.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(u)} className="text-xs font-bold uppercase tracking-wider text-blue-600 underline mr-3" data-testid={`edit-user-${u.id}`}>
                    <Pencil size={12} strokeWidth={2.5} className="inline mr-1" /> Editar
                  </button>
                  {u.id !== user.id && (
                    <button onClick={() => remove(u)} className="text-xs font-bold uppercase tracking-wider text-red-600 underline" data-testid={`del-user-${u.id}`}>
                      <Trash2 size={12} strokeWidth={2.5} className="inline mr-1" /> Eliminar
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500">Sin usuarios.</td></tr>}
          </tbody>
        </table>
      </div>

      {creating && <CreateUserModal onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
      {editing && <EditUserModal user={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">{children}</th>;
}

function CreateUserModal({ onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await api.post("/users", form); onDone(); }
    catch (e) { setError(e.response?.data?.detail || "Error"); }
    setLoading(false);
  };

  return (
    <Modal title="Nuevo usuario" onClose={onClose}>
      <form onSubmit={submit} className="p-6 space-y-4" data-testid="create-user-form">
        <Field label="Nombre completo" required value={form.name} onChange={update("name")} testId="user-name" />
        <Field label="Correo" type="email" required value={form.email} onChange={update("email")} mono testId="user-email" />
        <Field label="Contraseña (mín. 6)" type="password" required value={form.password} onChange={update("password")} testId="user-password" />
        {error && <div className="status-danger p-3 text-sm">{error}</div>}
        <button type="submit" disabled={loading} className="w-full h-12 bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-600 disabled:opacity-60" data-testid="save-user-btn">
          {loading ? "Creando..." : "Crear usuario"}
        </button>
      </form>
    </Modal>
  );
}

function EditUserModal({ user, onClose, onDone }) {
  const [form, setForm] = useState({ name: user.name, new_password: "" });
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { name: form.name };
      if (form.new_password) payload.new_password = form.new_password;
      await api.put(`/users/${user.id}`, payload);
      onDone();
    } catch (e) { setError(e.response?.data?.detail || "Error"); }
  };

  return (
    <Modal title={`Editar · ${user.email}`} onClose={onClose}>
      <form onSubmit={submit} className="p-6 space-y-4">
        <Field label="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} testId="user-edit-name" />
        <div>
          <label className="veta-tag block mb-2 flex items-center gap-2"><KeyRound size={12} strokeWidth={2.5} /> Nueva contraseña (opcional)</label>
          <input type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} placeholder="Dejar en blanco para no cambiar" className="w-full h-12 px-3 border border-gray-300" data-testid="user-edit-password" />
        </div>
        {error && <div className="status-danger p-3 text-sm">{error}</div>}
        <button type="submit" className="w-full h-12 bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-600" data-testid="save-user-edit">Guardar</button>
      </form>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center md:p-6">
      <div className="bg-white border-4 border-black w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-display text-2xl">{title}</h2>
          <button onClick={onClose}><X size={24} strokeWidth={2.5} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
